<template>
  <div>
    <h1 class="title">Accounts</h1>
    <div v-if="tableDisplay">
      <v-row>
        <v-col md="3" class="ml-md-auto">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
    </div>
    <div align="right">
      <v-data-table
        v-if="tableDisplay"
        :headers="headers"
        :items="accountList"
        :search="search"
        class="elevation-1 py-5"
        :sort-by="['accountId']"
        :sort-desc="[false]"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        hide-default-footer
        @page-count="pageCount = $event"
        dense
      >
        <!-- hide-default-footer -->
        <template v-slot:[`item.info`]="{ item }">
          <v-btn
            v-if="item"
            fab
            dark
            color="cyan"
            x-small
            @click="userDetails('READ', item)"
          >
            <v-icon dark> info </v-icon>
          </v-btn>
        </template>

        <template v-slot:[`item.approvedDate`]="{ item }">
          {{ changeDateFormat(item.approvedDate) }}
        </template>

        <template v-slot:[`item.accountType`]="{ item }">
          <v-row align="center" justify="center" class="px-0 mx-0">
            <v-col cols="12" sm="8" class="pa-0 ma-0">
              <v-select
                v-model="item.accountType"
                :items="accountTypes"
                class="py-2"
                dense
                solo
                hide-details="false"
                @input="changeAccountType($event, item)"
              ></v-select>
            </v-col>
          </v-row>
        </template>

        <template v-slot:[`item.disable`]="{ item }">
          <v-row align="center" justify="center" class="pa-0 ma-0">
            <v-col cols="12" sm="8" class="pa-0 ma-0">
              <v-switch
                v-model="item.disable"
                :false-value="true"
                :true-value="false"
                class="pa-0 ma-0"
                hide-details="false"
                @change="onToggleChange(item)"
              ></v-switch>
            </v-col>
          </v-row>
        </template>
      </v-data-table>

      <div class="text-center pt-2" v-if="accountList.length > itemsPerPage">
        <v-pagination v-model="page" :length="pageCount"></v-pagination>
      </div>
    </div>

    <v-snackbar v-model="snackbar">
      You have selected the Account Number: {{ currentItem }}
      <v-btn color="pink" text @click="snackbar = false"> Close </v-btn>
    </v-snackbar>

    <!-- User Details Card -->
    <v-dialog v-model="userCompanyDetailsForm" width="550px">
      <v-card>
        <!-- User Details Card Heading -->
        <v-toolbar color="primary" dark>
          <div id="block_container">
            <v-icon color="white" large left class="pl-0"
              >mdi-clipboard-play-outline</v-icon
            >
            <h2 class="text-left font-weight-bold">
              {{ userCompanyDetails.accountId }}
            </h2>
          </div>
        </v-toolbar>
        <!-- User Details Card Form -->
        <v-form
          ref="userCompanyDetailsForm"
          @submit.prevent="noop"
          class="pa-3"
        >
          <!-- User company details -->
          <v-row>
            <!-- Company Name -->
            <v-col cols="12" class="px-4 pt-4 pb-0">
              <v-text-field
                solo
                label="Company Name"
                prepend-icon="mdi-domain"
                v-model="userCompanyDetails.companyName"
                :readonly="userCompanyReadOnlyFields.companyName"
                required
                @input="saveButtonStatus('update', $event)"
              >
                <template v-slot:prepend>
                  <v-icon color="green darken-2">mdi-domain</v-icon>
                </template>
              </v-text-field>
            </v-col>
            <!-- Company Phone -->
            <v-col cols="12" class="px-4 py-0">
              <v-text-field
                solo
                label="Company Phone"
                prepend-icon="mdi-phone"
                v-model="userCompanyDetails.companyPhone"
                :readonly="userCompanyReadOnlyFields.companyPhone"
                :rules="userCompanyPhoneRules"
                required
                @input="saveButtonStatus('update', $event)"
              >
                <template v-slot:prepend>
                  <v-icon color="green darken-2">mdi-phone</v-icon>
                </template>
              </v-text-field>
            </v-col>
            <!-- Company Address -->
            <v-col cols="12" class="px-4 py-0">
              <v-textarea
                solo
                label="Company Address"
                prepend-icon="mdi-map-marker"
                v-model="userCompanyDetails.companyAddress"
                :readonly="userCompanyReadOnlyFields.companyAddress"
                required
                @input="saveButtonStatus('update', $event)"
                dense
              >
                <template v-slot:prepend>
                  <v-icon color="green darken-2">mdi-map-marker</v-icon>
                </template>
              </v-textarea>
            </v-col>
          </v-row>
          <!-- Associated Users Table -->
          <v-row
            v-if="
              userCompanyDetails.users && userCompanyDetails.users.length > 0
            "
            cols="12"
            class="justify-center pb-5"
          >
            <v-data-table
              v-if="userCompanyDetails.users.length > 0"
              :headers="associatedUserTableHeaders"
              :items="userCompanyDetails.users"
              class="elevation-1"
              :page.sync="page"
              hide-default-footer
              dense
              :sort-by.sync="associatedUserTableSortBy"
              :sort-desc.sync="associatedUserTableSortDesc"
            >
              <!-- Table header -->
              <template v-slot:top>
                <v-toolbar flat>
                  <v-toolbar-title>Associated Users</v-toolbar-title>
                </v-toolbar>
              </template>
              <!-- Kind -->
              <template v-slot:[`item.createdBy`]="{ item }">
                {{
                  item.createdBy && item.createdBy === "system"
                    ? "Primary"
                    : "Secondary"
                }}
              </template>
              <!-- Status toggle -->
              <template v-slot:[`item.disable`]="{ item }">
                <v-row align="center" justify="center" class="pa-0 ma-0">
                  <v-col cols="12" sm="8" class="pa-0 ma-0">
                    <v-switch
                      v-model="item.disable"
                      :false-value="true"
                      :true-value="false"
                      class="py-3 ma-0"
                      hide-details="false"
                      @change="associatedUserStatusChange(item)"
                    ></v-switch>
                    <v-overlay :value="associatedUserTableOverlay">
                      <v-progress-circular
                        indeterminate
                        size="120"
                        width="7"
                        color="grey darken-2"
                      ></v-progress-circular>
                    </v-overlay>
                  </v-col>
                </v-row>
              </template>
            </v-data-table>
          </v-row>
          <!-- Action buttons -->
          <v-btn
            color="error"
            class="mr-4 mb-4"
            align="left"
            @click="closeUserCompanyDetailsForm"
          >
            CLOSE
          </v-btn>
          <v-btn
            color="primary"
            class="mb-4"
            :disabled="userCompanyDetailsFormValidity"
            @click="dialogConfirmation = true"
            align="center"
          >
            SAVE
          </v-btn>
          <ConfirmDialog
            v-if="dialogConfirmation"
            :confirmDialogShow="dialogConfirmation"
            @confirm-dialog-close="dialogConfirmation = false"
            @confirm-dialog-save="userDetails('UPDATE', userCompanyDetails)"
          />
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { authComputed } from "../store/helpers.js";
import { mapMutations } from "vuex";
import admin from "../api/adminOps";

export default {
  components: {
    ConfirmDialog: () => import("@/components/ConfirmDialog"),
  },
  computed: {
    ...authComputed,
  },
  created() {
    const userAccounts = this.$store.getters["users/getUserAccounts"];

    this.accountList = userAccounts.filter(
      (account) => account.approvedBy && account.approvedBy.length > 0
    );
    if (this.accountList.length > 0) {
      this.tableDisplay = true;
    }
  },
  data() {
    return {
      accountTypes: ["trial", "full"],
      snackbar: false,
      search: "",
      currentItem: "",
      dialogConfirmation: false,
      headers: [
        {
          text: "Info",
          sortable: false,
          align: "start",
          value: "info",
        },
        { text: "Company Name", value: "companyName" },
        { text: "Account #", value: "accountId" },
        { text: "Platform", value: "platform" },
        { text: "CRM", value: "crmInstance" },
        { text: "Approve Name", value: "approvedBy" },
        { text: "Approve Date", value: "approvedDate" },
        { text: "Account Type", value: "accountType" }, //TO-DO check accountType exact field name in server
        { text: "Status", value: "disable" },
      ],
      accountList: [],
      associatedUserTableHeaders: [
        { text: "Kind", value: "createdBy", align: "start", sortable: false },
        { text: "Name", value: "adminName" },
        { text: "Email", value: "adminEmail" },
        { text: "Status", value: "disable" },
      ],
      associatedUserTableOverlay: false,
      associatedUserTableSortBy: "createdBy",
      associatedUserTableSortDesc: false,
      tableDisplay: false,
      page: 1,
      pageCount: 0,
      itemsPerPage: 8, // 8 - when using external pagination,
      userCompanyDetails: {},
      userCompanyDetailsForm: false,
      userCompanyReadOnlyFields: {
        companyAddress: false,
        companyName: false,
        companyPhone: false,
      },
      userCompanyDetailsFormValidity: true,
      userCompanyPhoneRules: [
        (value) => {
          value = value.replace(/\s/g, "");
          if (Number.isInteger(Number(value))) return true;
          else return "This field accepts numerics only.";
        },
      ],
    };
  },
  methods: {
    associatedUserStatusChange(item) {
      this.associatedUserTableOverlay = true;
      let payload = {
        recordId: item.id,
        disable: item.disable,
      };
      admin
        .changeUserStatus(payload)
        .then((response) => {
          this.associatedUserTableOverlay = false;
          this.showSnack(
            ` ${item.disable ? "Disbaled" : "Enabled"} User Successfully!`,
            false
          );
          this.scrollToTop();
          //sessionStorage modification
          let storageData = JSON.parse(sessionStorage.getItem("user"));
          storageData.value.data.forEach((element) => {
            if (element.adminId === item.id) {
              element.disable = response.value.data.disable;
            }
          });
          sessionStorage.setItem("user", JSON.stringify(storageData));

          //commit SET_USER_ACCOUNTS keep updated data without refresh
          this.$store.commit("users/SET_USER_ACCOUNTS", storageData.value.data);
          this.accountList = storageData.value.data;
        })
        .catch((error) => {
          this.associatedUserTableOverlay = false;
          console.error(
            `[AdminAccountDashboard] - onToggleChange - error ${JSON.stringify(
              error
            )}`
          );
          item.disable = !item.disable;
          this.scrollToTop();
          this.showSnack(`Account status updation failed.`, true);
        });
    },
    changeAccountType(event, item) {
      let adminData = this.$store.getters["users/getAdminData"];
      let postObj = {
        strict: true,
        value: {
          op: "UPDATEACCOUNTTYPE",
          id: item.accId,
          accountId: item.accountId,
          sessionId: adminData.sessionId,
          data: {
            accountType: event,
          },
        },
      };
      admin
        .adminOps(postObj)
        .then((response) => {
          this.showSnack(`Account type updated successfully.`, false);

          //sessionStorage modification
          let storageData = JSON.parse(sessionStorage.getItem("user"));
          storageData.value.data.forEach((element) => {
            if (element.accountId === item.accountId) {
              Object.assign(element, response.value.data);
            }
          });
          sessionStorage.setItem("user", JSON.stringify(storageData));

          //commit SET_USER_ACCOUNTS keep updated data without refresh
          this.$store.commit("users/SET_USER_ACCOUNTS", storageData.value.data);
        })
        .catch((error) => {
          console.error(
            `AdminAccountDashboard] - changeAccountType - error ${JSON.stringify(
              error
            )}`
          );
          this.showSnack(`Account type updation failed.`, true);
        });
    },
    changeDateFormat(timestamp) {
      let date = new Date(parseInt(timestamp));
      return [date.getMonth() + 1, date.getDate(), date.getFullYear()].join(
        "/"
      );
    },
    ...mapMutations({
      setSnack: "setSnack",
    }),
    closeUserCompanyDetailsForm() {
      (this.userCompanyDetails = {
        companyAddress: "",
        companyName: "",
        companyPhone: "",
      }),
        (this.userCompanyReadOnlyFields = {
          companyAddress: false,
          companyName: false,
          companyPhone: false,
        }),
        this.resetUserDetailsValidation();
      this.userCompanyDetailsFormValidity = true;
      this.userCompanyDetailsForm = false;
    },
    onToggleChange(item) {
      let adminData = this.$store.getters["users/getAdminData"];
      let postObj = {
        strict: true,
        value: {
          op: item.disable ? "DISABLEACCOUNT" : "ENABLEACCOUNT",
          accountId: item.accountId,
          id: item.accId,
          sessionId: adminData.sessionId,
          data: {
            crmInstance: item.crmInstance,
            approvedDate: item.approvedDate,
            companyName: item.companyName,
            status: item.disable === false ? "approved" : "denied",
            approvedBy: adminData.username,
            disable: item.disable,
          },
        },
      };
      admin
        .adminOps(postObj)
        .then((response) => {
          this.showSnack(
            ` ${
              postObj.value.op === "ENABLEACCOUNT" ? "Enabled" : "Disbaled"
            } Account Number: ${item.accountId}`,
            false
          );

          //sessionStorage modification
          let storageData = JSON.parse(sessionStorage.getItem("user"));
          storageData.value.data.forEach((element) => {
            if (element.accountId === item.accountId) {
              element.disable = item.disable;
            }
          });
          sessionStorage.setItem("user", JSON.stringify(storageData));

          //commit SET_USER_ACCOUNTS keep updated data without refresh
          this.$store.commit("users/SET_USER_ACCOUNTS", storageData.value.data);
        })
        .catch((error) => {
          console.error(
            `[AdminAccountDashboard] - onToggleChange - error ${JSON.stringify(
              error
            )}`
          );
          item.disable = !item.disable;
          this.showSnack(`Account status updation failed.`, true);
        });
    },
    resetUserDetailsValidation() {
      this.$refs.userCompanyDetailsForm.resetValidation();
    },
    saveButtonStatus(action, event) {
      if (
        this.userCompanyReadOnlyFields.companyAddress &&
        this.userCompanyReadOnlyFields.companyName &&
        this.userCompanyReadOnlyFields.companyPhone
      ) {
        this.userCompanyDetailsFormValidity = true;
      } else if (action === "update" && event !== "") {
        this.userCompanyDetailsFormValidity = false;
      } else {
        this.userCompanyDetailsFormValidity = true;
      }
    },
    showSnack: function (snack, isError) {
      const setSnackObj = {
        message: snack,
        isError: !!isError,
      };
      this.setSnack(setSnackObj);
    },
    async userDetails(eventName, item) {
      // Make api call to fetch record info
      let payload = {
        accountId: item.accountId,
        method: eventName === "READ" ? "get" : "patch",
        data: eventName === "READ" ? {} : item,
      };
      let axiosRequestStatus = true;
      if (eventName !== "READ") {
        this.dialogConfirmation = false;
        this.userCompanyDetailsForm = false;
      } else {
        let recentCompanyDetails =
          this.$store.getters["admin/getUserAccountCompanyInfo"];

        if (payload.accountId === recentCompanyDetails.accountId) {
          axiosRequestStatus = false;
          this.userCompanyDetails = recentCompanyDetails;
          this.userCompanyDetailsForm = true;
        }
      }

      if (axiosRequestStatus) {
        admin
          .fetchCompanyDetails(payload)
          .then((response) => {
            if (eventName === "READ") {
              this.userCompanyDetails = response.value
                ? response.value.data
                : [];
              if (Object.keys(this.userCompanyDetails).length) {
                if (this.userCompanyDetails.companyAddress != "")
                  this.userCompanyReadOnlyFields.companyAddress = true;
                if (this.userCompanyDetails.companyName != "")
                  this.userCompanyReadOnlyFields.companyName = true;
                if (this.userCompanyDetails.companyPhone != "")
                  this.userCompanyReadOnlyFields.companyPhone = true;
              }
              this.userCompanyDetailsForm = true;
              //TODO - strore current visitor accountID in store
              this.$store.commit(
                "admin/SET_USER_ACCOUNT_COMPANY_INFO",
                this.userCompanyDetails
              );
            } else {
              this.showSnack(
                `Updated user company details successfully!`,
                false
              );
            }
          })
          .catch((error) => {
            console.error(
              `[AdminAccountDashboard] - userDetails - error ${JSON.stringify(
                error
              )}`
            );
            if (eventName === "READ") {
              this.showSnack(
                `Fetching user company details got failed! Please try again!`,
                true
              );
            } else {
              this.showSnack(
                `Updating user company details got failed! Please try again!`,
                true
              );
            }
          });
      }
    },
    scrollToTop() {
      window.scrollTo(0, 0);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .v-data-table {
  max-width: 95% !important;
}
::v-deep .v-data-table th {
  font-size: 1.1rem !important;
  font-weight: bold !important;
}
::v-deep .v-data-table td {
  font-size: 0.9rem !important;
}

::v-deep .v-select__selections {
  width: 1rem;
}
.v-snack__content {
  padding-top: 0px;
  padding-bottom: 0px;
  padding-right: 16px;
  padding-left: 16px;
}
#block_container {
  display: flex;
}
</style>

